import {
  collection,
  query,
  where,
  getDocs,
  getCountFromServer,
  getDoc,
  doc,
  orderBy,
  limit,
  Timestamp,
  writeBatch,
} from "firebase/firestore";
import { db } from "@/config/firebase";
import {
  COLLECTIONS,
  CLAIM_STATUS,
  TRIAL_DURATION_DAYS,
} from "@/config/constants";
import { ClaimStatus, ClaimRequest } from "@/types";
import {
  sendClaimApprovedNotification,
  sendClaimRejectedNotification,
} from "./notificationsService";
import { barsService } from "./barsService";

/**
 * Claims Service
 * Handles all Firestore operations related to bar claims
 */
export const claimsService = {
  /**
   * Get pending claims
   */
  getPendingClaims: async (limitCount = 20): Promise<ClaimRequest[]> => {
    try {
      const claimsRef = collection(db, COLLECTIONS.BAR_CLAIMS);
      const q = query(
        claimsRef,
        where("status", "==", CLAIM_STATUS.PENDING),
        orderBy("requestedAt", "desc"),
        limit(limitCount),
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as ClaimRequest[];
    } catch (error) {
      console.error("Error fetching pending claims:", error);
      throw new Error("Fehler beim Laden der pending Claims");
    }
  },

  /**
   * Get all claims with optional status filter
   */
  getClaims: async (
    status?: ClaimStatus,
    limitCount = 20,
  ): Promise<ClaimRequest[]> => {
    try {
      const claimsRef = collection(db, COLLECTIONS.BAR_CLAIMS);
      let q = query(
        claimsRef,
        orderBy("requestedAt", "desc"),
        limit(limitCount),
      );

      if (status) {
        q = query(
          claimsRef,
          where("status", "==", status),
          orderBy("requestedAt", "desc"),
          limit(limitCount),
        );
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as ClaimRequest[];
    } catch (error) {
      console.error("Error fetching claims:", error);
      throw new Error("Fehler beim Laden der Claims");
    }
  },

  /**
   * Get single claim by ID
   */
  getClaim: async (claimId: string): Promise<ClaimRequest | null> => {
    try {
      const claimRef = doc(db, COLLECTIONS.BAR_CLAIMS, claimId);
      const claimDoc = await getDoc(claimRef);

      if (!claimDoc.exists()) {
        return null;
      }

      return {
        id: claimDoc.id,
        ...claimDoc.data(),
      } as ClaimRequest;
    } catch (error) {
      console.error("Error fetching claim:", error);
      throw new Error("Fehler beim Laden des Claims");
    }
  },

  /**
   * Get claims statistics (LEGACY - for BAR_CLAIMS collection)
   */
  getClaimsStats: async (): Promise<{
    pending: number;
    approved: number;
    rejected: number;
  }> => {
    try {
      const claimsRef = collection(db, COLLECTIONS.BAR_CLAIMS);

      const [pendingSnapshot, approvedSnapshot, rejectedSnapshot] =
        await Promise.all([
          getCountFromServer(
            query(claimsRef, where("status", "==", CLAIM_STATUS.PENDING)),
          ),
          getCountFromServer(
            query(claimsRef, where("status", "==", CLAIM_STATUS.APPROVED)),
          ),
          getCountFromServer(
            query(claimsRef, where("status", "==", CLAIM_STATUS.REJECTED)),
          ),
        ]);

      return {
        pending: pendingSnapshot.data().count,
        approved: approvedSnapshot.data().count,
        rejected: rejectedSnapshot.data().count,
      };
    } catch (error) {
      console.error("Error fetching claims stats:", error);
      throw new Error("Fehler beim Laden der Claims-Statistiken");
    }
  },

  /**
   * Approve a claim (Admin only)
   * Updates: claimRequest, bar, user, sends notification
   * @param deleteOldContent - wenn true: Drinks/Events/Offers des alten Owners werden gelöscht
   */
  approveClaim: async (
    claimId: string,
    adminUid: string,
    barName: string,
    deleteOldContent = false,
  ): Promise<void> => {
    try {
      // 1. Get Claim Data
      const claimDoc = await getDoc(
        doc(db, COLLECTIONS.CLAIM_REQUESTS, claimId),
      );
      if (!claimDoc.exists()) {
        throw new Error("Claim nicht gefunden");
      }

      const claimData = claimDoc.data() as ClaimRequest;

      if (claimData.status !== CLAIM_STATUS.PENDING) {
        throw new Error("Claim wurde bereits bearbeitet");
      }

      // Calculate trial dates
      const now = Timestamp.now();
      const trialEndDate = new Date(
        now.toMillis() + TRIAL_DURATION_DAYS * 24 * 60 * 60 * 1000,
      );

      // Use Firestore Batch for atomic operation
      const batch = writeBatch(db);

      // 2. Update Claim Request
      const claimRef = doc(db, COLLECTIONS.CLAIM_REQUESTS, claimId);
      batch.update(claimRef, {
        status: CLAIM_STATUS.APPROVED,
        reviewedAt: now,
        reviewedBy: adminUid,
        "trial.startDate": now,
        "trial.endDate": Timestamp.fromDate(trialEndDate),
      });

      // 3. Update Bar Document
      const barRef = doc(db, COLLECTIONS.BARS, claimData.barId);
      batch.update(barRef, {
        ownerId: claimData.userId,
        ownerName: claimData.ownerName,
        ownerEmail: claimData.userEmail,
        ownerPhone: claimData.contactPhone,
        isClaimed: true,
        claimedAt: now,
        hasPendingClaim: false,
        pendingClaimId: null,
        pendingClaimUserId: null,
      });

      // 4. Update User Document
      const userRef = doc(db, COLLECTIONS.USERS, claimData.userId);

      // Get current user data to merge owner.ownedBars array
      const userDoc = await getDoc(userRef);
      const userData = userDoc.exists() ? userDoc.data() : {};
      const currentOwnerOwnedBars = userData.owner?.ownedBars || [];

      // Merge barId into owner.ownedBars if not already present
      const updatedOwnerOwnedBars = currentOwnerOwnedBars.includes(
        claimData.barId,
      )
        ? currentOwnerOwnedBars
        : [...currentOwnerOwnedBars, claimData.barId];

      batch.update(userRef, {
        // Owner fields
        "owner.isOwner": true,
        "owner.ownedBars": updatedOwnerOwnedBars,

        // Subscription fields (root-level - NEW structure)
        hasActiveSubscription: true,
        subscriptionType: "trial",
        subscriptionStartDate: now,
        subscriptionEndDate: Timestamp.fromDate(trialEndDate),

        // Subscription fields (nested - OLD structure for compatibility)
        "subscription.tier": "trial",
        "subscription.status": "trial",
        "subscription.isTrialActive": true,
        "subscription.startDate": now,
        "subscription.endDate": Timestamp.fromDate(trialEndDate),
        "subscription.trialStartDate": now,
        "subscription.trialEndDate": Timestamp.fromDate(trialEndDate),
      }); // Commit batch
      await batch.commit();

      // 5. Alte Inhalte löschen wenn gewünscht (nach commit, non-kritisch)
      if (deleteOldContent) {
        try {
          await barsService.deleteBarContent(claimData.barId);
        } catch (e) {
          console.error(
            "Content-Deletion nach Claim-Approve fehlgeschlagen:",
            e,
          );
          // Kein throw — Claim selbst war erfolgreich
        }
      }

      // 6. Send Notification (after successful commit)
      await sendClaimApprovedNotification(
        claimData.userId,
        barName,
        claimId,
        claimData.barId,
      );

    } catch (error) {
      console.error("Error approving claim:", error);
      throw error;
    }
  },

  /**
   * Reject a claim (Admin only)
   * Updates: claimRequest, bar (clear pending), sends notification
   */
  rejectClaim: async (
    claimId: string,
    adminUid: string,
    rejectionReason: string,
    barName: string,
  ): Promise<void> => {
    try {
      // 1. Get Claim Data
      const claimDoc = await getDoc(
        doc(db, COLLECTIONS.CLAIM_REQUESTS, claimId),
      );
      if (!claimDoc.exists()) {
        throw new Error("Claim nicht gefunden");
      }

      const claimData = claimDoc.data() as ClaimRequest;

      if (claimData.status !== CLAIM_STATUS.PENDING) {
        throw new Error("Claim wurde bereits bearbeitet");
      }

      const now = Timestamp.now();

      // Use Firestore Batch for atomic operation
      const batch = writeBatch(db);

      // 2. Update Claim Request
      const claimRef = doc(db, COLLECTIONS.CLAIM_REQUESTS, claimId);
      batch.update(claimRef, {
        status: CLAIM_STATUS.REJECTED,
        reviewedAt: now,
        reviewedBy: adminUid,
        rejectionReason: rejectionReason,
      });

      // 3. Update Bar Document (clear pending claim if this claim is still pending)
      const barRef = doc(db, COLLECTIONS.BARS, claimData.barId);
      const barDoc = await getDoc(barRef);

      if (barDoc.exists()) {
        const barData = barDoc.data();
        // Only clear if this claim was the pending one
        if (barData.pendingClaimId === claimId) {
          batch.update(barRef, {
            hasPendingClaim: false,
            pendingClaimId: null,
            pendingClaimUserId: null,
          });
        }
      }

      // Commit batch
      await batch.commit();

      // 4. Send Notification (after successful commit)
      await sendClaimRejectedNotification(
        claimData.userId,
        barName,
        claimId,
        claimData.barId,
        rejectionReason,
      );

      // TODO: Optional - Delete verification photos (GDPR)
      // This would require Firebase Admin SDK or Cloud Function
      // For now, photos will auto-delete after 30 days (per Storage Rules)
    } catch (error) {
      console.error("Error rejecting claim:", error);
      throw error;
    }
  },

  /**
   * Get validation warnings for a claim
   * Checks for edge cases: already claimed bar, multiple claims, etc.
   */
  getClaimWarnings: async (claimId: string): Promise<string[]> => {
    try {
      const warnings: string[] = [];

      const claimDoc = await getDoc(
        doc(db, COLLECTIONS.CLAIM_REQUESTS, claimId),
      );
      if (!claimDoc.exists()) {
        return ["Claim nicht gefunden"];
      }

      const claimData = claimDoc.data() as ClaimRequest;

      // Check 1: Bar already has owner
      const barDoc = await getDoc(doc(db, COLLECTIONS.BARS, claimData.barId));
      if (barDoc.exists()) {
        const barData = barDoc.data();
        if (barData.ownerId && barData.ownerId !== claimData.userId) {
          warnings.push(
            `⚠️ Bar hat bereits einen Owner: ${
              barData.ownerName || barData.ownerId
            }`,
          );
        }
      }

      // Check 2: Multiple pending claims for same bar
      const competingClaims = await getDocs(
        query(
          collection(db, COLLECTIONS.CLAIM_REQUESTS),
          where("barId", "==", claimData.barId),
          where("status", "==", CLAIM_STATUS.PENDING),
        ),
      );

      if (competingClaims.size > 1) {
        warnings.push(
          `⚠️ Es gibt ${competingClaims.size} konkurrierende Claims für diese Bar`,
        );
      }

      // Check 3: User already owns a bar
      const userBars = await getDocs(
        query(
          collection(db, COLLECTIONS.BARS),
          where("ownerId", "==", claimData.userId),
        ),
      );

      if (userBars.size >= 1) {
        warnings.push(
          `⚠️ User besitzt bereits ${userBars.size} Bar(s). Für weitere Bars ist ein Custom Plan erforderlich.`,
        );
      }

      // Check 4: Verification photos exist (basic check)
      if (
        !claimData.verification?.selfieUrl ||
        !claimData.verification?.barUrl
      ) {
        warnings.push("⚠️ Verification-Fotos fehlen oder wurden gelöscht");
      }

      return warnings;
    } catch (error) {
      console.error("Error getting claim warnings:", error);
      return ["Fehler beim Prüfen der Warnings"];
    }
  },

  /**
   * Get Claim Stats
   */
  getClaimStats: async (): Promise<{
    pending: number;
    approved: number;
    rejected: number;
  }> => {
    try {
      const claimsRef = collection(db, COLLECTIONS.BAR_CLAIMS);

      const [pendingSnapshot, approvedSnapshot, rejectedSnapshot] =
        await Promise.all([
          getDocs(
            query(claimsRef, where("status", "==", CLAIM_STATUS.PENDING)),
          ),
          getDocs(
            query(claimsRef, where("status", "==", CLAIM_STATUS.APPROVED)),
          ),
          getDocs(
            query(claimsRef, where("status", "==", CLAIM_STATUS.REJECTED)),
          ),
        ]);

      return {
        pending: pendingSnapshot.size,
        approved: approvedSnapshot.size,
        rejected: rejectedSnapshot.size,
      };
    } catch (error) {
      console.error("Error fetching claim stats:", error);
      throw new Error("Fehler beim Laden der Claim-Statistiken");
    }
  },
};
